/**
 * Проигрыш — представление без стора, всё приходит пропсами. Одна главная кнопка (ISSUE-127): реванш, иначе полоса
 * таймера с «Пропустить», иначе «В бой». Совет прокачки — строка-кнопка, стартовый пакет и VIP — тонкая строка внизу.
 */
import { BoostIcon, Button, HeroBadge, Icon, PayGuard, WindowShell, compactCoins } from "../kit";
import {
  REVIVE_BOOST_PICKS,
  REVIVE_LIMIT,
  RETRY_LOCK_MS,
  type ReviveOffer,
  SKIP_STARS,
  type StarterOffer,
  VIP_STARS,
} from "@newfungame/contracts";
import { Stars } from "../offers";
import { type UpgradeAdvice } from "./model";
import { getLocale } from "../../../i18n";
import { nameKey } from "../format";
import { useT } from "../../../app";
import "./styles.css";

export interface DefeatViewProps {
  wave: number;
  rollbackTo: number;
  /** Монеты игрока — остаются после проигрыша. */
  coins: number;
  advice: UpgradeAdvice | null;
  onUpgrade(): void;
  onFight(): void;
  /** Секунд до конца таймера; 0 или нет — можно в бой. */
  lockSeconds?: number;
  /** Stars доступны (Mini App); иначе вместо покупок «Открыть в Telegram». */
  canPay?: boolean;
  /** Счёт открыт: кнопки покупок не жмутся. */
  busy?: boolean;
  /** Цены из состояния игрока: они же будут в счёте. */
  skipStars?: number;
  vipStars?: number;
  /** Реванш, который можно купить здесь; пока он есть, пропуск таймера не предлагаем. */
  revive?: ReviveOffer | null;
  onRevive?(): void;
  /** Стартовый пакет, один раз на игрока; пока он есть, вместо него не предлагаем VIP. */
  starter?: StarterOffer | null;
  onStarter?(): void;
  onSkip?(): void;
  /** Снять таймер билетом или кристаллами; `null` — нечем. */
  freeSkip?: { ticket: boolean; crystals: number } | null;
  onFreeSkip?(): void;
  /** Админ снимает таймер бесплатно; без колбэка кнопки нет. */
  onAdminSkip?(): void;
  onVip?(): void;
  onTelegram?(): void;
}

/** Реванш — главная кнопка; что именно вернётся, видно строкой под ней (MADR 0019), победа не обещается. */
function Revive({ offer, busy, onBuy }: { offer: ReviveOffer; busy: boolean; onBuy?(): void }) {
  const t = useT();
  return (
    <div class="win-defeat__main" data-testid="defeat-revive">
      <PayGuard>
        <Button tone="green" onClick={() => !busy && onBuy?.()} data-testid="defeat-revive-buy">
          {t("win.revive.title", { n: offer.wave })}{" "}
          {offer.stars > 0 ? <Stars n={offer.stars} /> : t("win.revive.free")}
        </Button>
      </PayGuard>
      <p class="win-note">
        {t("win.revive.effect", { n: REVIVE_BOOST_PICKS })}{" "}
        {t("win.revive.attempt", { n: offer.attempt, max: REVIVE_LIMIT })}
      </p>
      {offer.boosts.length > 0 && (
        <div class="win-defeat__boosts" data-testid="defeat-revive-boosts">
          <span class="win-note">{t("win.revive.boosts")}</span>
          {offer.boosts.map((id) => (
            <BoostIcon key={id} boostId={id} size={22} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Полоса таймера на месте кнопки боя: заполняется, пока идёт отсчёт. */
function WaitBar({ seconds }: { seconds: number }) {
  const t = useT();
  const done = Math.min(100, Math.max(0, 100 - (seconds * 1000 * 100) / RETRY_LOCK_MS));
  return (
    <div class="win-defeat__wait" data-testid="defeat-timer" role="timer">
      <span class="win-defeat__wait-fill" style={{ width: `${done}%` }} />
      <Icon name="clock" size={22} />
      <span>{t("win.defeat.wait", { s: seconds })}</span>
    </div>
  );
}

/** Как не ждать: бесплатно (админ, билет, кристаллы), за Stars или в Telegram. Пока есть реванш, платного пропуска нет. */
function SkipButtons(props: DefeatViewProps & { canPay: boolean; busy: boolean }) {
  const t = useT();
  return (
    <>
      {props.onAdminSkip && (
        <Button tone="blue" onClick={() => props.onAdminSkip?.()} data-testid="defeat-admin-skip">
          {t("win.defeat.skip")} {t("win.revive.free")}
        </Button>
      )}
      {props.freeSkip && (
        <Button tone="blue" onClick={() => props.onFreeSkip?.()} data-testid="defeat-free-skip">
          {t("win.defeat.skip")}{" "}
          {props.freeSkip.ticket ? (
            t("win.season.ticket")
          ) : (
            <span class="win-stars">
              <Icon name="crystal" size={20} /> {props.freeSkip.crystals}
            </span>
          )}
        </Button>
      )}
      {props.canPay
        ? !props.revive && (
            <PayGuard>
              <Button tone="blue" onClick={() => !props.busy && props.onSkip?.()} data-testid="defeat-skip">
                {t("win.defeat.skip")} <Stars n={props.skipStars ?? SKIP_STARS} />
              </Button>
            </PayGuard>
          )
        : !props.revive && (
            <Button tone="blue" onClick={() => props.onTelegram?.()} data-testid="defeat-telegram">
              {t("win.pay.telegram")}
            </Button>
          )}
    </>
  );
}

/** Окно поражения появляется мгновенно, поверх навыков: тап по навыку не должен открыть счёт (ISSUE-65), поэтому ждут только покупки. */
export function DefeatView(props: DefeatViewProps) {
  const { wave, rollbackTo, coins, advice, onUpgrade, onFight, lockSeconds = 0, canPay = false, busy = false } = props;
  const { revive = null, onRevive, starter = null, onStarter } = props;
  const locked = lockSeconds > 0;
  const t = useT();
  const locale = getLocale();
  const heroName = advice ? t(`${nameKey("hero", advice.heroId)}.name`) : "";
  // пройденный босс держит откат: волна может остаться той же
  const stay = rollbackTo >= wave;
  const backLine = t(stay ? "win.defeat.stay" : "win.defeat.back", { n: rollbackTo });
  const summary = stay ? backLine : t("win.defeat.summary", { wave, n: rollbackTo });
  return (
    <WindowShell kind="defeat" tone="red" title={t("win.defeat.title")}>
      <p class="win-defeat__summary">
        <span>{summary}</span>
        <span class="win-defeat__coins" data-testid="defeat-coins">
          <Icon name="coin" size={16} /> {t("win.defeat.coinsKept", { coins: compactCoins(coins, locale) })}
        </span>
      </p>
      {revive ? (
        <Revive offer={revive} busy={busy} onBuy={onRevive} />
      ) : locked ? (
        <WaitBar seconds={lockSeconds} />
      ) : (
        <Button tone="green" onClick={onFight} data-testid="defeat-fight">
          <Icon name="play" size={20} /> {t("win.defeat.fight")}
        </Button>
      )}
      {locked && !revive && <SkipButtons {...props} canPay={canPay} busy={busy} />}
      {advice && (
        <button type="button" class="win-defeat__advice" onClick={onUpgrade} data-testid="defeat-upgrade">
          <span class="win-defeat__advice-hero" data-testid="defeat-advice">
            <HeroBadge heroId={advice.heroId} name={heroName} />
            <span class="win-defeat__advice-text">
              {advice.kind === "heroLevel"
                ? t("win.defeat.adviceLevel", { hero: heroName, level: advice.to })
                : t("win.defeat.adviceStar", { hero: heroName, star: advice.to })}
              <b>{t("win.defeat.adviceGain", { pct: advice.damagePct })}</b>
            </span>
          </span>
          <span class="win-defeat__advice-cost">
            <Icon name="coin" size={18} /> {compactCoins(advice.cost, locale)}
          </span>
        </button>
      )}
      {revive && locked && (
        <>
          <p class="win-defeat__later" data-testid="defeat-timer" role="timer">
            <Icon name="clock" size={16} /> {t("win.defeat.wait", { s: lockSeconds })}
          </p>
          <SkipButtons {...props} canPay={canPay} busy={busy} />
        </>
      )}
      {revive && !locked && (
        <Button tone="link" onClick={onFight} data-testid="defeat-fight">
          {backLine}
        </Button>
      )}
      {starter ? (
        <PayGuard>
          <div data-testid="starter-offer">
            <button
              type="button"
              class="win-defeat__offer"
              onClick={() => !busy && onStarter?.()}
              data-testid="starter-buy"
            >
              <span>
                {t("win.defeat.starterLine", { coins: compactCoins(starter.coins, locale), days: starter.vipDays })}
              </span>
              <Stars n={starter.stars} />
            </button>
          </div>
        </PayGuard>
      ) : (
        locked &&
        canPay && (
          <PayGuard>
            <button
              type="button"
              class="win-defeat__offer"
              onClick={() => !busy && props.onVip?.()}
              data-testid="defeat-vip"
            >
              <span>{t("win.defeat.vipLine")}</span>
              <Stars n={props.vipStars ?? VIP_STARS} />
            </button>
          </PayGuard>
        )
      )}
    </WindowShell>
  );
}
